import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Badge } from "@/components/ui/badge";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import type { UserDTO } from "@/orval/model";
import {
  getUserManagementGetUserFilterConfigQueryOptions,
  getUserManagementGetUserFilterConfigQueryKey,
  userManagementGetAllUsersList,
  userManagementGetUserFilterConfig,
} from "@/orval/user-management";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { CheckCircle, XCircle } from "lucide-react";
import { useState } from "react";
import { UserDetailDialog } from "./-components/user-detail-dialog";

const columns: ColumnDef<UserDTO>[] = [
  {
    accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    size: 120,
    id: "fullName",
    header: "Name",
  },
  {
    accessorKey: "userName",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Username" />,
    enableSorting: true,
  },
  {
    accessorKey: "phoneNumber",
    header: "Phone Number",
    size: 120,
    enableSorting: false,
    cell: ({ row }) => row.original.phoneNumber || <span className="text-muted-foreground">N/A</span>,
  },
  {
    accessorKey: "emailConfirmed",
    header: "Email Confirmed",
    size: 110,
    enableSorting: false,
    cell: ({ row }) => {
      const confirmed = row.original.emailConfirmed;
      return (
        <Badge variant={confirmed ? "default" : "destructive"} className="flex items-center gap-1 w-fit">
          {confirmed ? <CheckCircle className="size-3" /> : <XCircle className="size-3" />}
          {confirmed ? "Yes" : "No"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "roles",
    header: "Global Roles",
    enableSorting: false,
    size: 80,
    cell: ({ row }) => {
      const roles = row.original.roles;
      if (!roles || roles.length === 0) {
        return <span className="text-muted-foreground">N/A</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {roles.map((role) => (
            <Badge key={role.name} variant="outline" className="text-xs">
              {role.name}
            </Badge>
          ))}
        </div>
      );
    },
  },
];

export const Route = createFileRoute("/_app/site-admin/users")({
  validateSearch: dataTableFiltersSearchSchema,
  loader: ({ location, ...loaderContext }) => {
    const { searchString, filters } = dataTableFiltersSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({
      searchString,
    });

    return ensureRouteQueriesData(loaderContext, [
      getUserManagementGetUserFilterConfigQueryOptions(),
      {
        queryKey: ["site-admin-users", queryOptions, filters],
        queryFn: () => userManagementGetAllUsersList(filters ?? [], queryOptions),
      },
    ]);
  },
  component: SiteAdminUsersPage,
});

function SiteAdminUsersPage() {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleRowClick = (user: UserDTO) => {
    setSelectedUserId(user.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUserId(null);
  };

  const getUsers = userManagementGetAllUsersList;
  const getUserFiltersConfig = userManagementGetUserFilterConfig;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        breadcrumbs={[{ label: "User Management" }]}
        title="User Management"
        subtitle="Manage all users across the platform"
      />

      <DataTable
        columns={columns}
        queryFn={(params, abortSignal, filters) => getUsers(filters ?? [], params, abortSignal)}
        queryKeyFn={(params, filters) => ["site-admin-users", params, filters]}
        filtersQueryFn={(abortSignal) => getUserFiltersConfig(abortSignal)}
        filtersQueryKeyFn={() => getUserManagementGetUserFilterConfigQueryKey()}
        onRowClick={handleRowClick}
        getRowIdFn={(row) => row.id.toString()}
      />

      <UserDetailDialog isOpen={isDialogOpen} onClose={handleCloseDialog} userId={selectedUserId} />
    </div>
  );
}
